import * as React from "react"
import { useMutation } from "react-query"
import { useForm } from "react-hook-form"
import { InputGroup, InputLeftAddon, Button, Text, Flex, Link, Box } from "@chakra-ui/react"

import { Form } from "../components/Form"
import { Input } from "../components/Input"
import { MultiSelect } from "../components/MultiSelect"
import { ClientAppEntity } from "../lib/types/clientApp"
import { fetcher } from "../lib/fetcher"
import { API_URL } from "../lib/config"
import { useToast } from "../lib/hooks/useToast"
import { downloadToFile } from "../lib/helpers/downloadToFile"
import { scopeOptions } from "../lib/static/clientApp"
import { RiInformationLine } from "react-icons/ri"
import { OrgEntity } from "../lib/types/org"

interface CreateClientAppResponse {
  uuid?: string
  private_key?: string
  label?: string
  detail?: {
    non_field_errors: [
      {
        message: string
        code: string
      },
    ]
  }
}

interface Props {
  organization: OrgEntity
  onClose: () => void
  refetch: () => void
}

export const ClientAppForm: React.FC<Props> = (props) => {
  const form = useForm()
  const toast = useToast()
  const [success, setSuccess] = React.useState(false)
  const [fileName, setFileName] = React.useState("")

  const { mutateAsync: create, isLoading } = useMutation<
    CreateClientAppResponse,
    unknown,
    Partial<ClientAppEntity>
  >((data) => fetcher("client_apps/", { body: JSON.stringify(data), method: "post" }))

  const defaultValues = {
    name: "",
    scopes: [] as { label: string; value: string }[],
  }

  const handleSubmit = async (values: typeof defaultValues) => {
    try {
      const data = {
        name: values.name,
        label: values.name,
        scopes: values.scopes.map((scope) => scope.value),
        organization: `${API_URL}organizations/${props.organization.uuid}/`,
      }
      const res = await create(data)
      if (res?.uuid && res?.private_key && res?.label) {
        props.refetch()
        const fileName = res?.label.replace(/ /g, "-").toLowerCase() + "-pkey.pem"
        downloadToFile(res.private_key, fileName, "text/plain")
        setSuccess(true)
        setFileName(fileName)
        toast({ description: "Successfully created Client Application Account" })
      } else if (res?.detail?.non_field_errors) {
        toast({ description: `Error: ${res.detail?.non_field_errors[0].message}`, status: "error" })
      } else {
        toast({ description: "Error creating Client Application Account", status: "error" })
      }
    } catch (error) {
      toast({ title: "Network error", description: "Please check your internet connection", status: "error" })
    }
  }

  return (
    <>
      {success ? (
        <>
          <Text>
            Your new client application account has been created successfully! Its corresponding private key:
          </Text>
          <Text fontWeight="bold" my={2}>
            {fileName}
          </Text>
          <Text>
            has been downloaded. Use this key to obtain access to the different resources in our API.
          </Text>
          <Text mt={2}>
            For more information, check out{" "}
            <Link
              isExternal
              href="https://docs.api.noa.one/authentication-and-authorisation.html"
              color="gray.500"
            >
              our docs
            </Link>
          </Text>
          <Flex align="center" border="1px solid" borderColor="gray.100" p={2} my={4} borderRadius="lg">
            <Box as={RiInformationLine} w="35px" mx={2} />
            <Text ml={1} fontSize="0.8rem">
              This key is only made available to you at this time, store it in a secure place on your system!
            </Text>
          </Flex>
          <Flex justify="flex-end">
            <Button onClick={props.onClose} colorScheme="green">
              Ok!
            </Button>
          </Flex>
        </>
      ) : (
        <Form {...form} onSubmit={handleSubmit}>
          <InputGroup>
            <InputLeftAddon w="85px">Name</InputLeftAddon>
            <Input name="name" isRequired borderTopLeftRadius={0} borderBottomLeftRadius={0} />
          </InputGroup>
          <InputGroup zIndex={99}>
            <InputLeftAddon w="85px">Scopes</InputLeftAddon>
            <MultiSelect
              options={scopeOptions}
              isRequired
              name="scopes"
              borderTopLeftRadius={0}
              borderBottomLeftRadius={0}
            />
          </InputGroup>
          <InputGroup zIndex={1}>
            <Button
              width="100%"
              colorScheme="green"
              type="submit"
              isDisabled={isLoading}
              isLoading={isLoading}
            >
              Create
            </Button>
          </InputGroup>
        </Form>
      )}
    </>
  )
}
